import { useViewedTenantAccount } from './useViewedTenantAccount';

type EntitlementKey = 'checkInCheckOut' | 'accessManagement';

export interface EntitlementState {
    /** The tenant holds the add-on. `false` while still resolving — read
     *  `isResolving` before treating that as "does not hold it". */
    entitled: boolean;
    /**
     * True while `getAccount` is in flight and has produced no answer yet.
     *
     * The distinction is not cosmetic. A gate that renders "not entitled"
     * during load, behind a `<Navigate replace>`, strands the user on the
     * refusal page permanently — the redirect happens on the first render and
     * the later answer never brings them back. Since ADR-0001 Amendment 4 that
     * page is an upsell, so the failure mode is a sales pitch shown to a tenant
     * who already pays for the add-on. Wait on this; never redirect through it.
     */
    isResolving: boolean;
    /**
     * The lookup ran and did not answer.
     *
     * Separate from `isResolving` because a gate that waits must be able to
     * STOP waiting, and `isResolving` alone cannot say whether the answer is
     * still coming. Without it the access-management route could only ever show
     * a spinner — which is what QA #2439 defect B saw on `fun`: an empty
     * `<main>`, a spinner, no error, no empty state, nothing to report.
     */
    hasFailed: boolean;
}

/**
 * Shared resolver for a per-tenant add-on entitlement (ADR 0001). Reads the
 * flag serialized onto the account; the backend resolves it by union (a solution
 * that implies the capability OR the explicit add-on entitlement). A local-only
 * dev override (resolved by the caller and passed in) is honored only outside
 * production, so it can never grant the entitlement in a deployed build.
 *
 * It answers for the tenant **in view**, not for the caller's own. That
 * distinction is the whole point of going through `useViewedTenantAccount`:
 * a bare `getAccount` resolves by the caller's auth0_sub, so for a master on
 * another tenant it returns the MASTER's entitlements under that tenant's name
 * — which is how a venue tenant with no device kept its access-management entry
 * while every gate believed it was asking about that tenant (#1334, #2059 are
 * the same mistake on other screens). The by-slug master read carries the same
 * serialized `entitlements`, so there is no reason to answer from the wrong
 * tenant.
 *
 * A legacy tenant has no operational row and both reads are skipped, so every
 * key resolves `false` here with nothing pending: capabilities for those
 * tenants are decided by their Auth0 permissions, never by this.
 *
 * The caller passes the override already evaluated from a literal
 * `process.env.REACT_APP_*` check — CRA only inlines literal references, so the
 * env read must stay in the caller, not here.
 */
export function useEntitlementState(
    key: EntitlementKey,
    devOverride: boolean,
): EntitlementState {
    const { accountTenant, isResolving, hasFailed } = useViewedTenantAccount({
        // Stripe status is a live round-trip and no entitlement depends on it.
        withPaymentIntegration: false,
    });

    if (process.env.NODE_ENV !== 'production' && devOverride) {
        return { entitled: true, isResolving: false, hasFailed: false };
    }

    return {
        entitled: Boolean(accountTenant?.entitlements?.[key]),
        // Settled — success or error — means we trust the answer and stop
        // waiting, mirroring `useCanAccessAccount`.
        isResolving,
        hasFailed,
    };
}

/**
 * The boolean-only view, for surfaces where "not yet known" and "not entitled"
 * may safely render the same — a section that shows its locked state until the
 * answer arrives, say. A ROUTE GUARD is not such a surface: use
 * `useEntitlementState` there and wait on `isResolving`.
 */
export function useEntitlement(key: EntitlementKey, devOverride: boolean): boolean {
    return useEntitlementState(key, devOverride).entitled;
}
